import { z } from 'zod';
import { daysBetweenInclusive } from './absence.js';
import { StaffLevel } from './admin-users.js';
import { CalendarDate } from './project.js';
/**
 * Nhân sự × dự án trong một kỳ — APM-TPR, tab "Đội của tôi" ở trang Hiệu suất.
 * Mỗi ô là (một người, một dự án): giờ bấm trong kỳ, task xong trong kỳ và
 * ước lượng của chúng, task đang giao. Web tự gộp thành tổng quan, theo người
 * hay theo dự án — một nguồn số, ba cách nhìn, không lệch nhau.
 */
export const TEAM_PROJECTS_MAX_DAYS = 366;
export const TEAM_PROJECTS_MAX_MEMBERS = 300;
export const TeamProjectsQuery = z
    .object({ from: CalendarDate, to: CalendarDate })
    .superRefine((input, ctx) => {
    if (input.from > input.to) {
        ctx.addIssue({
            code: 'custom',
            path: ['to'],
            message: 'Ngày kết thúc phải sau ngày bắt đầu',
        });
        return;
    }
    if (daysBetweenInclusive(input.from, input.to) > TEAM_PROJECTS_MAX_DAYS) {
        ctx.addIssue({
            code: 'custom',
            path: ['to'],
            message: `Kỳ không dài quá ${TEAM_PROJECTS_MAX_DAYS} ngày`,
        });
    }
});
export const TeamProjectsCell = z.object({
    userId: z.string(),
    projectId: z.string(),
    /** Giờ bấm trong kỳ, cắt theo kỳ; đoạn đang mở tính tới lúc đọc. */
    hours: z.number().min(0),
    /** Task xong trong kỳ (hiện vẫn xong), quy cho `velocity_sample.user_id ?? assignee_id`. */
    tasksDone: z.number().int().min(0),
    /** Tổng ước lượng của các task xong ấy — so với giờ đã bỏ ra. Task không ước lượng không cộng. */
    doneEstimateHours: z.number().min(0),
    /** Tổng story point của các task xong ấy. Task không có điểm không cộng. */
    doneStoryPoints: z.number().int().min(0),
    /** Task **đang** giao cho người ấy ở dự án ấy, chưa xong — "đang làm dự án nào" lúc đọc. */
    tasksOpen: z.number().int().min(0),
    /**
     * % **đăng ký**: giờ cam kết (APM-1310b) trong kỳ ÷ giờ theo lịch gốc trong kỳ,
     * tính theo từng thứ. Cam kết theo % ra đúng số đó; theo giờ/ngày thì quy theo
     * lịch (bị chặn bởi trần của từng ngày). `null` = PM chưa đặt cam kết.
     *
     * % **thực tế** không nằm ở đây: nó là `hours` ÷ tổng `hours` của người ấy
     * trong kỳ — web tự chia, để tổng quan, theo người và theo dự án cùng một phép.
     */
    registeredPct: z.number().min(0).max(100).nullable(),
});
/**
 * Tải của một người trong một tuần — cùng nguồn với cột Workload của báo cáo đội
 * (`WorkloadService`): giờ việc được giao trải theo ngày làm việc ÷ giờ làm
 * được (lịch gốc trừ nghỉ phép, ngày lễ). > 100% = quá tải.
 */
export const TeamProjectsLoad = z.object({
    committedHours: z.number().min(0),
    capacityHours: z.number().min(0),
    /** `null` khi không có giờ làm nào (nghỉ cả tuần). */
    pct: z.number().min(0).nullable(),
});
export const TeamProjectsReport = z.object({
    from: CalendarDate,
    to: CalendarDate,
    timezone: z.string(),
    capturedAt: z.string(),
    /** `org` = cả tổ chức (quyền không điều kiện), `chain` = cả nhánh cấp dưới. */
    scope: z.enum(['org', 'chain']),
    /** Mọi người trong phạm vi, kể cả người không có ô nào — "không làm gì trong kỳ" cũng là một câu trả lời. */
    people: z
        .array(z.object({
        userId: z.string(),
        name: z.string(),
        /** Bậc trong tổ chức (quản trị xếp); `null` = chưa xếp bậc — trạng thái thật, không phải thiếu dữ liệu. */
        level: StaffLevel.nullable(),
        /**
         * Số dự án người ấy **đang tham gia** — là thành viên (chưa rời) của dự án
         * đang chạy (lập kế hoạch / đang thực hiện / tạm dừng; không demo), dù có
         * việc trong kỳ hay không. Khác số ô của người ấy: ô chỉ có ở dự án có giờ,
         * task hay cam kết.
         */
        memberProjectCount: z.number().int().min(0),
        /** Giờ theo lịch gốc trong kỳ — mẫu số của % đăng ký. */
        scheduledHours: z.number().min(0),
        /** Tải tuần này (tính cả việc đã xong từ đầu tuần) — không phụ thuộc kỳ đang chọn. */
        thisWeek: TeamProjectsLoad,
        /** Tải của `windows.ahead` — N ngày tới, N = độ dài kỳ đang chọn. */
        ahead: TeamProjectsLoad,
    }))
        .max(TEAM_PROJECTS_MAX_MEMBERS),
    /** Hai cửa sổ của cột capacity (múi giờ tổ chức): tuần này và N ngày tới. */
    weeks: z.object({
        thisWeek: z.object({ from: CalendarDate, to: CalendarDate }),
        /**
         * N ngày tới tính từ **ngày mai**, N = số ngày của kỳ đang chọn (người dùng chốt
         * 2026-09-28: chọn 4 ngày thì xem 4 ngày tới, 90 ngày thì 90 ngày tới). Luôn
         * nhìn tới trước từ hôm nay, kể cả khi kỳ đang chọn nằm trong quá khứ.
         */
        ahead: z.object({ from: CalendarDate, to: CalendarDate, days: z.number().int().min(1) }),
    }),
    projects: z.array(z.object({ projectId: z.string(), key: z.string(), name: z.string() })),
    cells: z.array(TeamProjectsCell),
});
//# sourceMappingURL=team-projects.js.map